import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  archiveConversation,
  listConversationHistory,
  listDurableConversations,
  unarchiveConversation,
  type ConversationArchiveState,
  type ConversationCapabilities,
  type DurableConversation,
} from "@/api/conversations";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { hydrateConversationMessages } from "@/desktop/conversation-hydration";
import type { ConversationMessage } from "@/stores/conversation-store";
import {
  conversationBrowserAccountKey,
  historyCache,
  invalidateListCache,
  listCache,
  listCacheKey,
  uniqueById,
} from "./conversation-browser-cache";
import {
  conversationBrowserErrorMessage,
  conversationBrowserNotice,
} from "./conversation-browser-errors";
import { useConversationDelete } from "./use-conversation-delete";
import { useConversationSelect } from "./use-conversation-select";

type LoadState = "idle" | "loading" | "ready" | "error";

const DEFAULT_CAPABILITIES: ConversationCapabilities = {
  search: false,
  delete: false,
};
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_QUERY_LENGTH = 2;

export { clearConversationBrowserCache } from "./conversation-browser-cache";

interface ConversationBrowserOptions {
  projectId?: string | null;
  initialArchiveState?: ConversationArchiveState;
  limit?: number;
  /** False: the list stays empty and idle, and no request is sent. */
  enabled?: boolean;
}

export function useConversationBrowser(options?: ConversationBrowserOptions) {
  const scopeKey = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    conversationBrowserAccountKey,
    () => null,
  );
  const projectId = options?.projectId ?? null;
  const requestLimit = options?.limit;
  const limit = requestLimit ?? 50;
  const enabled = options?.enabled ?? true;
  const [archiveState, setArchiveState] = useState<ConversationArchiveState>(
    () => options?.initialArchiveState ?? "active",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [capabilities, setCapabilities] =
    useState<ConversationCapabilities>(DEFAULT_CAPABILITIES);
  const [rows, setRows] = useState<DurableConversation[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [listState, setListState] = useState<LoadState>("idle");
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DurableConversation | null>(null);
  const [history, setHistory] = useState<ConversationMessage[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyOrdering, setHistoryOrdering] = useState("");
  const [historyState, setHistoryState] = useState<LoadState>("idle");
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [online, setOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  const onlineRef = useRef(online);
  const listRequestRef = useRef(0);
  const historyRequestRef = useRef(0);
  const historyRef = useRef<ConversationMessage[]>([]);
  const selectedRef = useRef<DurableConversation | null>(null);
  const previousOnlineRef = useRef(online);
  const listFirstInFlightRef = useRef(false);
  const listMoreInFlightRef = useRef(false);
  const historyFirstInFlightRef = useRef(false);
  const historyMoreInFlightRef = useRef(false);
  const mutationInFlightRef = useRef(false);

  const normalizedDebouncedQuery = debouncedSearchQuery
    .trim()
    .replace(/\s+/g, " ");
  const normalizedSearchQuery =
    normalizedDebouncedQuery.length >= MIN_SEARCH_QUERY_LENGTH
      ? normalizedDebouncedQuery
      : "";
  const listKey = scopeKey
    ? listCacheKey(
        scopeKey,
        archiveState,
        normalizedSearchQuery,
        limit,
        projectId,
      )
    : null;

  useEffect(() => {
    if (!capabilities.search) {
      setSearchQuery("");
      setDebouncedSearchQuery("");
      return;
    }
    if (!searchQuery) {
      setDebouncedSearchQuery("");
      return;
    }
    const timer = window.setTimeout(
      () => setDebouncedSearchQuery(searchQuery),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [capabilities.search, searchQuery]);

  const updateSearchQuery = useCallback(
    (query: string) => {
      if (query && !capabilities.search) return;
      setSearchQuery(query);
    },
    [capabilities.search],
  );

  const loadFirst = useCallback(async () => {
    listMoreInFlightRef.current = false;
    setLoadingMore(false);
    if (!enabled) return;
    if (!scopeKey || !listKey) {
      setRows([]);
      setCapabilities(DEFAULT_CAPABILITIES);
      setListState("error");
      setListError(conversationBrowserNotice("signInToViewChats"));
      return;
    }
    const cached = listCache.get(listKey);
    if (cached) {
      setRows(cached.rows);
      setNextCursor(cached.nextCursor);
      setHasMore(cached.hasMore);
      setCapabilities(cached.capabilities);
    } else {
      setRows([]);
      setNextCursor(null);
      setHasMore(false);
    }
    if (!onlineRef.current) {
      setListState(cached ? "ready" : "error");
      setListError(
        cached ? null : conversationBrowserNotice("offlineNoCachedChats"),
      );
      return;
    }
    const requestId = ++listRequestRef.current;
    const isCurrentScope = captureDesktopChatAuthScopeGuard();
    listFirstInFlightRef.current = true;
    setListState(cached ? "ready" : "loading");
    setListError(null);
    try {
      const page = await listDurableConversations({
        projectId,
        archiveState,
        query: normalizedSearchQuery || undefined,
        ...(requestLimit ? { limit: requestLimit } : {}),
      });
      if (requestId !== listRequestRef.current || !isCurrentScope()) return;
      if (normalizedSearchQuery && !page.capabilities.search) {
        setCapabilities(page.capabilities);
        setSearchQuery("");
        setDebouncedSearchQuery("");
        return;
      }
      const next = {
        scopeKey,
        rows: page.data,
        nextCursor: page.pagination.next_cursor,
        hasMore: page.pagination.has_more,
        capabilities: page.capabilities,
      };
      listCache.set(listKey, next);
      setRows(next.rows);
      setNextCursor(next.nextCursor);
      setHasMore(next.hasMore);
      setCapabilities(next.capabilities);
      setListState("ready");
    } catch (error) {
      if (requestId !== listRequestRef.current || !isCurrentScope()) return;
      setListError(conversationBrowserErrorMessage(error));
      setListState(cached ? "ready" : "error");
    } finally {
      if (requestId === listRequestRef.current && isCurrentScope()) {
        listFirstInFlightRef.current = false;
      }
    }
  }, [
    archiveState,
    enabled,
    listKey,
    normalizedSearchQuery,
    requestLimit,
    scopeKey,
    projectId,
  ]);

  useEffect(() => {
    setSelected(null);
    setHistory([]);
    void loadFirst();
    return () => {
      listRequestRef.current += 1;
      historyRequestRef.current += 1;
    };
  }, [loadFirst]);

  useEffect(() => {
    const update = () => {
      onlineRef.current = navigator.onLine;
      setOnline(navigator.onLine);
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      void loadFirst();
    };
    window.addEventListener("denker-conversations-changed", refresh);
    return () =>
      window.removeEventListener("denker-conversations-changed", refresh);
  }, [loadFirst]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const loadMore = useCallback(async () => {
    if (
      !online ||
      !scopeKey ||
      !listKey ||
      !nextCursor ||
      listFirstInFlightRef.current ||
      listMoreInFlightRef.current
    ) {
      return;
    }
    listMoreInFlightRef.current = true;
    const requestId = ++listRequestRef.current;
    const isCurrentScope = captureDesktopChatAuthScopeGuard();
    setLoadingMore(true);
    setListError(null);
    try {
      const page = await listDurableConversations({
        projectId,
        archiveState,
        query: normalizedSearchQuery || undefined,
        cursor: nextCursor,
        ...(requestLimit ? { limit: requestLimit } : {}),
      });
      if (requestId !== listRequestRef.current || !isCurrentScope()) return;
      if (normalizedSearchQuery && !page.capabilities.search) {
        setCapabilities(page.capabilities);
        setSearchQuery("");
        setDebouncedSearchQuery("");
        return;
      }
      setRows((current) => {
        const merged = uniqueById([...current, ...page.data]);
        listCache.set(listKey, {
          scopeKey,
          rows: merged,
          nextCursor: page.pagination.next_cursor,
          hasMore: page.pagination.has_more,
          capabilities: page.capabilities,
        });
        return merged;
      });
      setNextCursor(page.pagination.next_cursor);
      setHasMore(page.pagination.has_more);
      setCapabilities(page.capabilities);
    } catch (error) {
      if (requestId === listRequestRef.current && isCurrentScope()) {
        setListError(conversationBrowserErrorMessage(error));
      }
    } finally {
      listMoreInFlightRef.current = false;
      if (requestId === listRequestRef.current && isCurrentScope()) {
        setLoadingMore(false);
      }
    }
  }, [
    archiveState,
    listKey,
    nextCursor,
    normalizedSearchQuery,
    online,
    requestLimit,
    scopeKey,
    projectId,
  ]);

  const loadHistory = useCallback(
    async (conversation: DurableConversation, cursor?: string) => {
      if (!scopeKey) return;
      if (cursor && historyMoreInFlightRef.current) return;
      if (cursor && historyFirstInFlightRef.current) return;
      const key = `${scopeKey}:${conversation.id}`;
      const cached = historyCache.get(key);
      if (!cursor && cached) {
        setHistory(cached.rows);
        setHistoryCursor(cached.nextCursor);
        setHistoryHasMore(cached.hasMore);
        setHistoryOrdering(cached.ordering);
      }
      if (!onlineRef.current) {
        setHistoryState(cached ? "ready" : "error");
        setHistoryError(
          cached ? null : conversationBrowserNotice("chatNotOffline"),
        );
        return;
      }
      if (cursor) historyMoreInFlightRef.current = true;
      const requestId = ++historyRequestRef.current;
      const isCurrentScope = captureDesktopChatAuthScopeGuard();
      if (cursor) setLoadingMoreHistory(true);
      else {
        historyFirstInFlightRef.current = true;
        setHistoryState(cached ? "ready" : "loading");
      }
      setHistoryError(null);
      try {
        const page = await listConversationHistory(conversation.id, { cursor });
        const hydrated = await hydrateConversationMessages(page.data);
        if (requestId !== historyRequestRef.current || !isCurrentScope())
          return;
        const merged = cursor
          ? uniqueById([...hydrated, ...historyRef.current])
          : hydrated;
        const entry = {
          rows: merged,
          nextCursor: page.pagination.next_cursor,
          hasMore: page.pagination.has_more,
          ordering: page.integrity?.ordering ?? "",
        };
        historyCache.set(key, entry);
        setHistory(entry.rows);
        setHistoryCursor(entry.nextCursor);
        setHistoryHasMore(entry.hasMore);
        setHistoryOrdering(entry.ordering);
        setHistoryState("ready");
      } catch (error) {
        if (requestId !== historyRequestRef.current || !isCurrentScope())
          return;
        setHistoryError(conversationBrowserErrorMessage(error));
        setHistoryState(cached && !cursor ? "ready" : "error");
      } finally {
        if (cursor) historyMoreInFlightRef.current = false;
        if (requestId === historyRequestRef.current && isCurrentScope()) {
          if (!cursor) historyFirstInFlightRef.current = false;
          setLoadingMoreHistory(false);
        }
      }
    },
    [scopeKey],
  );

  const selectConversation = useCallback(
    (conversation: DurableConversation) => {
      historyMoreInFlightRef.current = false;
      historyFirstInFlightRef.current = false;
      setLoadingMoreHistory(false);
      setSelected(conversation);
      setHistory([]);
      setHistoryCursor(null);
      setHistoryHasMore(false);
      void loadHistory(conversation);
    },
    [loadHistory],
  );

  const clearSelection = useCallback(() => {
    historyRequestRef.current += 1;
    historyMoreInFlightRef.current = false;
    historyFirstInFlightRef.current = false;
    setLoadingMoreHistory(false);
    setSelected(null);
  }, []);

  useEffect(() => {
    const wasOnline = previousOnlineRef.current;
    previousOnlineRef.current = online;
    if (wasOnline || !online) return;
    void loadFirst();
    const currentSelection = selectedRef.current;
    if (currentSelection) void loadHistory(currentSelection);
  }, [loadFirst, loadHistory, online]);

  const mutateArchive = useCallback(
    async (conversation: DurableConversation) => {
      if (!online || mutationInFlightRef.current) return;
      mutationInFlightRef.current = true;
      setMutatingId(conversation.id);
      setListError(null);
      const isCurrentScope = captureDesktopChatAuthScopeGuard();
      try {
        if (conversation.archived_at) {
          await unarchiveConversation(conversation.id);
        } else {
          await archiveConversation(conversation.id);
        }
        if (!isCurrentScope()) return;
        if (scopeKey) invalidateListCache(scopeKey);
        setSelected(null);
        await loadFirst();
      } catch (error) {
        if (isCurrentScope())
          setListError(conversationBrowserErrorMessage(error));
      } finally {
        mutationInFlightRef.current = false;
        if (isCurrentScope()) setMutatingId(null);
      }
    },
    [loadFirst, online, scopeKey],
  );

  const mutateSelect = useConversationSelect({
    online,
    mutationInFlightRef,
    setMutatingId,
    setError: setListError,
  });
  const mutateDelete = useConversationDelete({
    enabled: capabilities.delete,
    online,
    scopeKey,
    loadFirst,
    mutationInFlightRef,
    setMutatingId,
    setSelected,
    setError: setListError,
  });

  return {
    archiveState,
    setArchiveState,
    searchQuery,
    setSearchQuery: updateSearchQuery,
    searchEnabled: capabilities.search,
    searchActive: normalizedSearchQuery.length > 0,
    appliedSearchQuery: normalizedSearchQuery,
    rows,
    listState,
    listError,
    hasMore,
    loadingMore,
    loadMore,
    refresh: loadFirst,
    selected,
    selectConversation,
    clearSelection,
    history,
    historyState,
    historyError,
    historyHasMore,
    loadingMoreHistory,
    historyOrdering,
    loadMoreHistory: () =>
      selected && historyCursor && !loadingMoreHistory
        ? loadHistory(selected, historyCursor)
        : Promise.resolve(),
    mutateArchive,
    mutateSelect,
    mutateDelete,
    mutatingId,
    deleteEnabled: capabilities.delete,
    online,
  };
}
